import { useNavigate } from 'react-router-dom'
import useNotificationStore from '@/store/useNotificationStore'
import { Button } from '@/components/ui/button'
import { formatDistanceToNow } from 'date-fns'

/**
 * The dropdown content showing the list of notifications.
 * Displays unread and read notifications with timestamps and allows marking as read.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Callback to close the dropdown
 *
 * @example
 * // Used inside PopoverContent
 * <PopoverContent>
 *   <NotificationDropdown onClose={() => setOpen(false)} />
 * </PopoverContent>
 */
const NotificationDropdown = ({ onClose }) => {
  const navigate = useNavigate()
  const { notifications, isLoading, markAsRead, markAllAsRead, unreadCount } = useNotificationStore()

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.isRead) {
      await markAsRead([notification.id])
    }

    // Navigate to task if it exists
    if (notification.taskId && notification.projectId) {
      navigate(`/projects/${notification.projectId}/tasks?task=${notification.taskId}`)
    }

    onClose()
  }

  const handleMarkAllRead = async () => {
    await markAllAsRead()
  }

  const formatTime = (timestamp) => {
    try {
      // timestamp is epoch milliseconds from the server
      // new Date() converts it to the browser's local timezone automatically
      const date = new Date(timestamp)
      return formatDistanceToNow(date, { addSuffix: true })
    } catch {
      return ''
    }
  }

  return (
    <div className="flex flex-col max-h-96" data-testid="notification-dropdown">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold">Notifications</h3>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllRead}
            className="text-xs text-blue-500 hover:text-blue-600"
            data-testid="mark-all-read-button"
          >
            Mark all read
          </Button>
        )}
      </div>

      {/* Notification List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-gray-500">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="no-notifications">
            No notifications
          </div>
        ) : (
          <ul>
            {notifications.map((notification) => (
              <li
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`px-4 py-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                  !notification.isRead ? 'bg-blue-50' : ''
                }`}
                data-testid="notification-item"
              >
                <div className="flex items-start gap-2">
                  {!notification.isRead && (
                    <span className="mt-1.5 h-2 w-2 bg-blue-500 flex-shrink-0" data-testid="unread-indicator" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {notification.title}
                    </p>
                    {notification.message && (
                      <p className="text-xs text-gray-600 truncate">
                        {notification.message}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTime(notification.createdAt)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default NotificationDropdown
